import { Controller } from "stimulus"

// #1977: afspraken verplaatsen door ze in het rooster te slepen.
//
// Met de muis: een blok vastpakken en een paar pixels bewegen begint het
// slepen (een gewone klik opent de afspraak zoals altijd). Op een
// aanraakscherm: het blok even ingedrukt houden; wie meteen beweegt,
// scrolt gewoon. De greep onderaan een blok verandert alleen de eindtijd.
//
// Tijdens het slepen wordt het origineel doorzichtig, volgt een kopie de
// muis of vinger en staat het nieuwe tijdslot (in stappen van 15 minuten,
// met dezelfde duur) gemarkeerd in de dag: groen kan, oranje is een
// waarschuwing, rood kan niet. Een botsing met een andere afspraak van
// dezelfde trainer ziet het rooster zelf; de rest vraagt het aan de server
// (verplaatsen/controle). Meerdere trainers tegelijk is gewoon goed.
//
// Loslaten opent het bevestigingsvenster (verplaatsen, in de frame
// "verplaatsen" van de dialog hieronder), met Van en Naar en bij een reeks
// de keuze waarop het van toepassing is. Na opslaan laadt alleen het rooster
// opnieuw (frame "rooster_inhoud"), zodat de banen meteen kloppen. Escape
// breekt het slepen af. Zonder JavaScript is er niets te slepen; dan blijft
// Bewerken in het afspraakvenster.
//
// Daarnaast ververst het rooster elke minuut stilletjes als niemand sleept
// en er geen venster open is, zodat wijzigingen van collega's verschijnen.
const STAP = 15
const DREMPEL = 5
const VASTHOUDEN = 450
const BEWEGING_TOUCH = 10
const RAND = 56
const VERVERSEN = 60000

const tijd = (minuut) => {
  const m = Math.max(0, Math.min(minuut, 24 * 60))
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`
}

export default class extends Controller {
  static targets = ["dialog", "frame", "laden", "melding"]
  static values = { allesBeheren: Boolean, weergave: String, teksten: Object }

  connect() {
    this.controles = new Map()
    this.bijIndrukken = (event) => this.indrukken(event)
    this.bijAanraken = (event) => this.aanraken(event)
    this.bijNatuurlijkSlepen = (event) => {
      if (event.target.closest?.(".ro-blok[data-sleepbaar]")) event.preventDefault()
    }
    this.bijMenu = (event) => {
      if (this.kandidaat?.aanraking || this.sleep) event.preventDefault()
    }
    this.bijVerzonden = (event) => {
      if (event.detail?.success) this.verversRooster()
    }
    this.element.addEventListener("pointerdown", this.bijIndrukken)
    this.element.addEventListener("touchstart", this.bijAanraken, { passive: true })
    this.element.addEventListener("dragstart", this.bijNatuurlijkSlepen)
    this.element.addEventListener("contextmenu", this.bijMenu)
    if (this.hasFrameTarget) this.frameTarget.addEventListener("turbo:submit-end", this.bijVerzonden)
    this.ververser = window.setInterval(() => this.automatischVerversen(), VERVERSEN)
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this.bijIndrukken)
    this.element.removeEventListener("touchstart", this.bijAanraken)
    this.element.removeEventListener("dragstart", this.bijNatuurlijkSlepen)
    this.element.removeEventListener("contextmenu", this.bijMenu)
    if (this.hasFrameTarget) this.frameTarget.removeEventListener("turbo:submit-end", this.bijVerzonden)
    window.clearInterval(this.ververser)
    this.stoppen()
  }

  // ---- de muis (en een pen) ----

  indrukken(event) {
    if (event.pointerType === "touch") return
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const blok = event.target.closest(".ro-blok[data-sleepbaar]")
    if (!blok || !this.element.contains(blok)) return

    const rekken = Boolean(event.target.closest("[data-greep]"))
    if (rekken) event.preventDefault()
    this.kandidaat = { blok, rekken, x: event.clientX, y: event.clientY, aanraking: false }
    this.bijBewegen = (e) => this.bewegen(e.clientX, e.clientY)
    this.bijLoslaten = () => this.loslaten()
    this.bijAfbreken = () => this.afbreken()
    this.bijToets = (e) => { if (e.key === "Escape") this.afbreken() }
    window.addEventListener("pointermove", this.bijBewegen)
    window.addEventListener("pointerup", this.bijLoslaten)
    window.addEventListener("pointercancel", this.bijAfbreken)
    window.addEventListener("keydown", this.bijToets)
  }

  bewegen(x, y) {
    if (!this.kandidaat) return
    if (!this.sleep) {
      if (Math.hypot(x - this.kandidaat.x, y - this.kandidaat.y) < DREMPEL) return
      this.beginnen(this.kandidaat)
    }
    this.volgen(x, y)
  }

  loslaten() {
    const gesleept = Boolean(this.sleep)
    if (gesleept) {
      this.onderdrukKlik()
      this.laatVallen()
    }
    this.stoppen()
  }

  afbreken() {
    if (this.sleep) this.onderdrukKlik()
    this.stoppen()
  }

  // ---- aanraken: even vasthouden, dan slepen ----

  aanraken(event) {
    if (event.touches.length !== 1 || this.sleep) return
    const blok = event.target.closest(".ro-blok[data-sleepbaar]")
    if (!blok || !this.element.contains(blok)) return

    const punt = event.touches[0]
    const kandidaat = { blok, rekken: Boolean(event.target.closest("[data-greep]")), x: punt.clientX, y: punt.clientY,
                        aanraking: true }
    this.kandidaat = kandidaat
    blok.classList.add("is-vastgehouden")
    this.wachter = window.setTimeout(() => {
      blok.classList.remove("is-vastgehouden")
      if (this.kandidaat !== kandidaat) return
      this.beginnen(kandidaat)
      if (navigator.vibrate) navigator.vibrate(12)
      this.volgen(kandidaat.x, kandidaat.y)
    }, VASTHOUDEN)
    this.bijVingerBewegen = (e) => this.vingerBewegen(e)
    this.bijVingerLos = (e) => this.vingerLos(e)
    window.addEventListener("touchmove", this.bijVingerBewegen, { passive: false })
    window.addEventListener("touchend", this.bijVingerLos, { passive: false })
    window.addEventListener("touchcancel", this.bijVingerLos, { passive: false })
  }

  vingerBewegen(event) {
    const punt = event.touches[0]
    if (!punt || !this.kandidaat) return
    if (!this.sleep) {
      // Eerst bewegen is scrollen: dan wordt er niet gesleept
      if (Math.hypot(punt.clientX - this.kandidaat.x, punt.clientY - this.kandidaat.y) > BEWEGING_TOUCH) this.stoppen()
      this.kandidaat && (this.kandidaat.x = punt.clientX, this.kandidaat.y = punt.clientY)
      return
    }
    event.preventDefault()
    this.volgen(punt.clientX, punt.clientY)
  }

  vingerLos(event) {
    if (this.sleep) {
      event.preventDefault()
      this.onderdrukKlik()
      if (event.type === "touchend") this.laatVallen()
    }
    this.stoppen()
  }

  // ---- slepen ----

  beginnen(kandidaat) {
    const blok = kandidaat.blok
    const raster = blok.closest("[data-rooster-raster]")
    const kolom = blok.closest(".ro-dag")
    if (!raster || !kolom) return

    const rechthoek = blok.getBoundingClientRect()
    const begin = Number(blok.dataset.beginMinuut)
    const eind = Number(blok.dataset.eindMinuut)
    this.sleep = {
      blok, raster, rekken: kandidaat.rekken, bronKolom: kolom,
      id: blok.dataset.afspraakId,
      bronDag: blok.dataset.datum,
      bronTrainer: blok.dataset.afspraakTrainer || "",
      begin, eind, duur: eind - begin,
      eersteUur: Number(raster.dataset.eersteUur), laatsteUur: Number(raster.dataset.laatsteUur),
      grijpX: kandidaat.x - rechthoek.left, grijpY: kandidaat.y - rechthoek.top,
      breed: rechthoek.width, hoog: rechthoek.height,
      x: kandidaat.x, y: kandidaat.y
    }
    blok.classList.add("is-opgepakt")
    this.element.classList.add("is-slepend")
    if (kandidaat.rekken) this.element.classList.add("is-rekken")

    if (!kandidaat.rekken) {
      const kloon = blok.cloneNode(true)
      kloon.removeAttribute("href")
      kloon.removeAttribute("data-action")
      kloon.removeAttribute("data-turbo-frame")
      kloon.setAttribute("aria-hidden", "true")
      kloon.classList.remove("is-opgepakt")
      kloon.classList.add("ro-sleepkloon")
      kloon.style.width = `${rechthoek.width}px`
      kloon.style.height = `${rechthoek.height}px`
      this.element.appendChild(kloon)
      this.sleep.kloon = kloon
    }
    this.sleep.label = document.createElement("div")
    this.sleep.label.className = "ro-sleeplabel"
    this.sleep.label.setAttribute("aria-hidden", "true")
    this.element.appendChild(this.sleep.label)
    this.sleep.voorbeeld = document.createElement("div")
    this.sleep.voorbeeld.className = "ro-sleepvoorbeeld"
    this.sleep.voorbeeld.setAttribute("aria-hidden", "true")
  }

  volgen(x, y) {
    const s = this.sleep
    if (!s) return
    s.x = x
    s.y = y
    this.randScrollen(y)

    if (s.kloon) {
      s.kloon.style.left = `${x - s.grijpX}px`
      s.kloon.style.top = `${y - s.grijpY}px`
    }
    s.label.style.left = `${x + 14}px`
    s.label.style.top = `${y + 16}px`

    let kolom, dag, begin, eind, trainer
    if (s.rekken) {
      kolom = s.bronKolom
      dag = s.bronDag
      begin = s.begin
      eind = this.afronden(this.minuutOp(kolom, y))
      eind = Math.max(begin + STAP, Math.min(eind, s.laatsteUur * 60))
      trainer = s.bronTrainer
    } else {
      kolom = this.kolomOp(x, y)
      if (kolom) {
        dag = kolom.dataset.dagKolom
        begin = this.afronden(this.minuutOp(kolom, y - s.grijpY))
        begin = Math.max(s.eersteUur * 60, Math.min(begin, (s.laatsteUur * 60) - s.duur))
        eind = begin + s.duur
        const kolomTrainer = kolom.dataset.trainerKolom
        if (kolomTrainer === undefined) trainer = s.bronTrainer
        else if (kolomTrainer === "open") trainer = s.bronTrainer === "" ? "" : "open"
        else trainer = kolomTrainer
      }
    }
    this.zetDoel(kolom, dag, begin, eind, trainer)
  }

  zetDoel(kolom, dag, begin, eind, trainer) {
    const s = this.sleep
    if (s.kolom !== kolom) {
      s.kolom?.classList.remove("is-doel")
      s.voorbeeld.remove()
      s.kolom = kolom
      if (kolom) {
        kolom.classList.add("is-doel")
        kolom.appendChild(s.voorbeeld)
      }
    }
    if (!kolom) {
      s.doel = null
      s.label.textContent = ""
      s.label.hidden = true
      return
    }

    s.label.hidden = false
    s.doel = { dag, begin, eind, trainer: trainer || "" }
    s.voorbeeld.style.setProperty("--ro-begin", begin - (s.eersteUur * 60))
    s.voorbeeld.style.setProperty("--ro-duur", eind - begin)
    const wanneer = `${this.weekdag(dag)} ${tijd(begin)}–${tijd(eind)}`
    const sleutel = `${dag} ${begin} ${eind} ${s.doel.trainer}`
    if (s.sleutel === sleutel) return

    s.sleutel = sleutel
    const tekst = (this.tekstenValue.nieuwe_tijd || "%{wanneer}").replace("%{wanneer}", wanneer)
    if (this.hasMeldingTarget) this.meldingTarget.textContent = tekst
    this.toonStatus(this.lokaleStatus(s.doel), tekst)
  }

  // Wat het rooster zelf al weet; anders vraagt het de server
  lokaleStatus(doel) {
    const s = this.sleep
    if (this.ongewijzigd(doel)) return { status: "", meldingen: [] }
    if (doel.trainer === "open" && s.bronTrainer !== "") return { status: "fout", meldingen: [this.tekstenValue.open_kolom] }
    if (doel.trainer !== s.bronTrainer && !this.allesBeherenValue) {
      return { status: "fout", meldingen: [this.tekstenValue.andere_trainer] }
    }

    if (doel.trainer && doel.trainer !== "open") {
      const botsing = Array.from(s.raster.querySelectorAll(`.ro-blok[data-afspraak-trainer="${doel.trainer}"]`)).find((blok) => {
        if (blok === s.blok || blok.dataset.afspraakId === s.id || blok.dataset.afspraakDag !== doel.dag) return false
        if ((blok.dataset.afspraakStatus || "").startsWith("geannuleerd")) return false
        return Number(blok.dataset.beginMinuut) < doel.eind && Number(blok.dataset.eindMinuut) > doel.begin
      })
      if (botsing) {
        const naam = botsing.dataset.trainerNaam || s.blok.dataset.trainerNaam || ""
        return { status: "fout", meldingen: [(this.tekstenValue.zelfde_trainer || "").replace("%{trainer}", naam)] }
      }
    }

    const sleutel = this.controleSleutel(doel)
    if (this.controles.has(sleutel)) return this.controles.get(sleutel)
    this.vraagControle(doel, sleutel)
    return { status: "bezig", meldingen: [this.tekstenValue.bezig] }
  }

  toonStatus(uitkomst, tekst) {
    const s = this.sleep
    if (!s) return
    const status = uitkomst.status
    s.status = status
    for (const element of [s.voorbeeld, s.label]) {
      element.classList.remove("is-ok", "is-waarschuwing", "is-fout", "is-bezig")
      if (status) element.classList.add(`is-${status}`)
    }
    const melding = (uitkomst.meldingen || [])[0] || ""
    s.label.textContent = tekst
    s.voorbeeld.innerHTML = ""
    const regel = document.createElement("span")
    regel.className = "ro-sleepvoorbeeld-tijd"
    regel.textContent = tekst
    s.voorbeeld.appendChild(regel)
    if (melding && status !== "ok") {
      const uitleg = document.createElement("span")
      uitleg.className = "ro-sleepvoorbeeld-melding"
      uitleg.textContent = melding
      s.voorbeeld.appendChild(uitleg)
    }
  }

  controleSleutel(doel) {
    return `${this.sleep.id}|${doel.dag}|${doel.begin}|${doel.eind}|${doel.trainer}`
  }

  vraagControle(doel, sleutel) {
    window.clearTimeout(this.controleWachter)
    const s = this.sleep
    const url = this.adres(s.blok.dataset.controle, doel)
    this.controleWachter = window.setTimeout(async () => {
      try {
        const antwoord = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
        const uitkomst = await antwoord.json()
        this.controles.set(sleutel, { status: uitkomst.status || "fout", meldingen: uitkomst.meldingen || [] })
      } catch (e) {
        this.controles.set(sleutel, { status: "", meldingen: [this.tekstenValue.netwerk] })
      }
      if (this.sleep && this.sleep.doel && this.controleSleutel(this.sleep.doel) === sleutel) {
        this.toonStatus(this.controles.get(sleutel), this.sleep.label.textContent)
      }
    }, 120)
  }

  laatVallen() {
    const s = this.sleep
    if (!s || !s.doel || this.ongewijzigd(s.doel)) return
    if (s.doel.trainer === "open" && s.bronTrainer !== "") return

    this.openVenster(this.adres(s.blok.dataset.verplaatsen, s.doel, { stand: s.blok.dataset.stand }))
  }

  // ---- het bevestigingsvenster ----

  openVenster(url) {
    if (!this.hasDialogTarget || !this.hasFrameTarget) {
      window.location.href = url
      return
    }
    if (this.hasLadenTarget) this.frameTarget.innerHTML = this.ladenTarget.innerHTML
    this.frameTarget.src = url
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
  }

  sluit(event) {
    event?.preventDefault()
    if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close()
  }

  achtergrond(event) {
    if (event.target === this.dialogTarget) this.dialogTarget.close()
  }

  gesloten() {
    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = ""
  }

  // ---- het rooster opnieuw laden ----

  verversRooster() {
    const frame = document.getElementById("rooster_inhoud")
    if (!frame) return
    this.controles.clear()
    const adres = window.location.href
    if (frame.getAttribute("src") === adres && typeof frame.reload === "function") {
      frame.reload()
    } else {
      frame.src = adres
    }
  }

  automatischVerversen() {
    if (document.hidden || this.sleep || this.kandidaat) return
    if (this.element.querySelector("dialog[open]")) return
    this.verversRooster()
  }

  // ---- hulpjes ----

  ongewijzigd(doel) {
    const s = this.sleep
    return doel.dag === s.bronDag && doel.begin === s.begin && doel.eind === s.eind && doel.trainer === s.bronTrainer
  }

  adres(basis, doel, extra = {}) {
    const url = new URL(basis, window.location.origin)
    url.searchParams.set("datum", doel.dag)
    url.searchParams.set("begintijd", tijd(doel.begin))
    url.searchParams.set("eindtijd", tijd(doel.eind % (24 * 60)))
    if (doel.trainer && doel.trainer !== "open" && doel.trainer !== this.sleep.bronTrainer) {
      url.searchParams.set("trainer_id", doel.trainer)
    }
    Object.entries(extra).forEach(([sleutel, waarde]) => { if (waarde) url.searchParams.set(sleutel, waarde) })
    return url.toString()
  }

  kolomOp(x, y) {
    const raster = this.sleep.raster
    return document.elementsFromPoint(x, y).find((element) => element.matches?.(".ro-dag[data-dag-kolom]") &&
                                                               raster.contains(element)) || null
  }

  minuutOp(kolom, y) {
    const s = this.sleep
    const rechthoek = kolom.getBoundingClientRect()
    const minuten = (s.laatsteUur - s.eersteUur) * 60
    const perMinuut = rechthoek.height / minuten
    return (s.eersteUur * 60) + ((y - rechthoek.top) / perMinuut)
  }

  afronden(minuut) {
    return Math.round(minuut / STAP) * STAP
  }

  weekdag(dag) {
    const [jaar, maand, dagnummer] = (dag || "").split("-").map(Number)
    const datum = new Date(jaar, maand - 1, dagnummer)
    const namen = this.tekstenValue.weekdagen || []
    return `${namen[datum.getDay()] || ""} ${dagnummer || ""}`.trim()
  }

  // Bij de rand van het venster schuift de pagina mee
  randScrollen(y) {
    window.cancelAnimationFrame(this.scrollLus)
    const snelheid = y < RAND ? -Math.ceil((RAND - y) / 4) : (y > window.innerHeight - RAND ? Math.ceil((y - (window.innerHeight - RAND)) / 4) : 0)
    if (!snelheid) return
    this.scrollLus = window.requestAnimationFrame(() => {
      if (!this.sleep) return
      window.scrollBy(0, snelheid)
      this.volgen(this.sleep.x, this.sleep.y)
    })
  }

  // Na slepen geen klik die de afspraak opent
  onderdrukKlik() {
    const tegenhouden = (event) => {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
    window.addEventListener("click", tegenhouden, { capture: true, once: true })
    window.setTimeout(() => window.removeEventListener("click", tegenhouden, { capture: true }), 400)
  }

  stoppen() {
    window.clearTimeout(this.wachter)
    window.clearTimeout(this.controleWachter)
    window.cancelAnimationFrame(this.scrollLus)
    if (this.bijBewegen) window.removeEventListener("pointermove", this.bijBewegen)
    if (this.bijLoslaten) window.removeEventListener("pointerup", this.bijLoslaten)
    if (this.bijAfbreken) window.removeEventListener("pointercancel", this.bijAfbreken)
    if (this.bijToets) window.removeEventListener("keydown", this.bijToets)
    if (this.bijVingerBewegen) window.removeEventListener("touchmove", this.bijVingerBewegen)
    if (this.bijVingerLos) {
      window.removeEventListener("touchend", this.bijVingerLos)
      window.removeEventListener("touchcancel", this.bijVingerLos)
    }
    this.kandidaat?.blok?.classList.remove("is-vastgehouden")
    const s = this.sleep
    if (s) {
      s.blok.classList.remove("is-opgepakt")
      s.kolom?.classList.remove("is-doel")
      s.kloon?.remove()
      s.label?.remove()
      s.voorbeeld?.remove()
    }
    this.element.classList.remove("is-slepend", "is-rekken")
    this.sleep = null
    this.kandidaat = null
  }
}
